import { useAuth } from '@/context/AuthContext';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import { useOpenAdminPanel } from '@/hooks/useOpenAdminPanel';

/**
 * Full-screen takeover shown ONLY when every org membership is paused (PR 6).
 * With no healthy org to switch to, there is no rail to fall back on — the
 * takeover replaces the whole shell. Headline uses the current org (or the
 * first membership); body copy matches LockedPaneCard by role. Never says
 * "suspended".
 */
export default function FullLockTakeover() {
  const { currentOrganization, organizations, signOut } = useAuth();
  const { lockInfoFor } = useOrgLockState();
  const { openAdminPanel, opening } = useOpenAdminPanel();

  const orgId = currentOrganization?.id ?? organizations[0]?.organization_id ?? null;
  const info = lockInfoFor(orgId);
  const orgName = info?.orgName ?? currentOrganization?.name ?? 'Your organization';
  const churchAdmin = Boolean(info?.isAdmin && !info?.isPersonal);
  const personalAdmin = Boolean(info?.isAdmin && info?.isPersonal);

  let body: string;
  if (churchAdmin) {
    body =
      'Your church’s plan needs attention. Update billing to restore access for everyone — nothing has been deleted.';
  } else if (personalAdmin) {
    body =
      'Your plan is billed through the App Store — open Flock on your iPhone or iPad to change it, or remove extra members. Nothing has been deleted.';
  } else {
    body =
      'This group’s plan needs attention. Your admin can restore access — nothing has been deleted, and everything will be right where you left it.';
  }

  return (
    <div className="flex h-screen items-center justify-center bg-gradient-to-b from-flock-navy-900 to-flock-navy-800 p-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#FEF3C7] text-4xl">
          🕊️
        </div>
        <h1 className="text-xl font-semibold text-flock-navy-900">{orgName} is paused</h1>
        <p className="mt-3 text-sm leading-relaxed text-flock-navy-600">{body}</p>

        {churchAdmin && (
          <button
            onClick={() => openAdminPanel()}
            disabled={opening}
            className="min-h-touch mt-6 inline-flex w-full items-center justify-center rounded-lg bg-flock-navy-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-flock-navy-700 disabled:opacity-60"
          >
            {opening ? 'Opening…' : 'Update Plan in Admin Panel'}
          </button>
        )}

        <button
          onClick={() => signOut()}
          className="min-h-touch mt-3 inline-flex w-full items-center justify-center rounded-lg px-6 py-3 text-sm font-semibold text-flock-navy-500 transition hover:bg-flock-navy-50"
        >
          Sign out
        </button>

        <p className="mt-6 text-xs text-flock-navy-400">Need help? support@flock.church</p>
      </div>
    </div>
  );
}
